import type { API_CapacityExceededResponse } from "@pilatestime/shared-types";
import type { AxiosError } from "axios";

/**
 * Narrows an unknown mutation/request error to the backend's typed
 * capacity-exceeded 409 payload.
 *
 * The backend returns HTTP 409 with `API_CapacityExceededResponse` when a GROUP
 * schedule (or one of its sessions) is at capacity. That status code is shared
 * with other 409 errors (e.g. duplicate enrollment), so callers must narrow by
 * the `error` discriminator in the body — not by the status alone.
 *
 * @returns the typed payload when the error is a capacity-exceeded 409,
 *          otherwise `null`.
 */
export function parseCapacityExceededError(
	error: unknown,
): API_CapacityExceededResponse | null {
	const axiosError = error as
		| AxiosError<Partial<API_CapacityExceededResponse>>
		| undefined;
	const data = axiosError?.response?.data;

	if (
		axiosError?.response?.status === 409 &&
		data?.error === "CAPACITY_EXCEEDED" &&
		(data.scope === "schedule" || data.scope === "session") &&
		typeof data.capacity === "number" &&
		typeof data.current === "number"
	) {
		return data as API_CapacityExceededResponse;
	}

	return null;
}
